import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  CheckboxGroup,
  DialogOverlay,
  DialogOverlayActions,
  Divider,
  Separator,
  TextField,
  useKeyboardShortcuts,
} from '@fs/zion-ui'
import { MenuFilter } from '@fs/zion-icon'
import { trackEvent } from '@fs/zion-analytics'

// Screen-reader-only: keeps the aria-live region announced without taking up space.
const visuallyHiddenCss = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

/**
 * Filters each group's options by label, keeping every group even when it has no match.
 *
 * @param {Array<{label: string, options: Array<{value: string, label: string}>}>} groups - The groups to filter.
 * @param {string} filterText - Text typed in the filter.
 * @returns {Array<Object>} Groups with only the matching options.
 */
const filterFieldGroups = (groups, filterText) => {
  const searchStr = filterText.trim().toLowerCase()
  if (!searchStr) return groups
  return groups.map((group) => ({
    ...group,
    options: group.options.filter((option) => option.label.toLowerCase().includes(searchStr)),
  }))
}

/**
 * The Add/Remove dialog for picking which field types a card shows, e.g. Add/Remove Additional
 * Facts (AdditionalFactsDialog). A filter and grouped checkboxes whose changes are staged: checking
 * only changes the dialog, Save hands the checked values to onSave, and Cancel (or Esc) discards
 * them. It can't be closed with an X or by clicking outside.
 *
 * @param {Object} props
 * @param {Object} props.overlay - Return value of useOverlay; open it with overlay.handleClick so focus returns to the opener.
 * @param {string} props.title - Dialog title.
 * @param {string} props.filterLabel - Label and placeholder of the filter field.
 * @param {Array<{label: string, options: Array<{value: string, label: string}>}>} props.groups - Checkbox groups.
 * @param {Array<string>} props.initialChecked - Values checked when the dialog opens.
 * @param {(checked: Set<string>) => void} props.onSave - Called with the checked values when Save is pressed.
 * @param {string} props.linkNamePrefix - Prefix for the Save/Cancel linkNames, e.g. 'additional-facts-dialog'.
 * @param {string} props.testIdPrefix - Prefix for data-testids.
 * @returns {JSX.Element}
 */
const AddRemoveFieldsDialog = ({
  overlay,
  title,
  filterLabel,
  groups,
  initialChecked,
  onSave,
  linkNamePrefix,
  testIdPrefix,
}) => {
  const [t] = useTranslation()
  const [filterText, setFilterText] = useState('')
  const [checked, setChecked] = useState(() => new Set(initialChecked))
  const filterRef = useRef(null)
  const { isOpen, close } = overlay

  // Re-seed the staged checks each time the dialog opens, so a cancelled change never lingers.
  useEffect(() => {
    if (isOpen) setChecked(new Set(initialChecked))
    // initialChecked is read only on open; later changes come from this dialog's own Save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const filteredGroups = useMemo(() => filterFieldGroups(groups, filterText), [groups, filterText])
  const resultCount = filteredGroups.reduce((count, group) => count + group.options.length, 0)

  const handleCancel = () => close()

  const handleSave = () => {
    onSave(checked)
    close()
  }

  // nonClosable turns off Zion's own Esc handling, so Esc is wired to Cancel here, linkName included.
  // Registering through Zion's shortcut stack keeps Esc from also reaching the image viewer (grid view).
  useKeyboardShortcuts({
    handlers: {
      Escape: {
        action: () => {
          trackEvent({ event_name: 'click_action', link_name: `${linkNamePrefix}-cancel` })
          close()
        },
      },
    },
    enabled: isOpen,
  })

  const toggle = (value) =>
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })

  return (
    <DialogOverlay
      {...overlay}
      title={title}
      nonClosable
      closeOnClickAway={false}
      focusRef={filterRef}
      onClose={() => setFilterText('')}
      data-testid={`${testIdPrefix}-dialog`}
      footer={
        <DialogOverlayActions>
          <Button
            color="gray"
            onClick={handleCancel}
            linkName={`${linkNamePrefix}-cancel`}
            data-testid={`${testIdPrefix}-cancel`}
          >
            {t('common-ui:cancel.action', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            onClick={handleSave}
            linkName={`${linkNamePrefix}-save`}
            data-testid={`${testIdPrefix}-save`}
          >
            {t('common-ui:save.action', 'Save')}
          </Button>
        </DialogOverlayActions>
      }
    >
      <TextField
        ref={filterRef}
        Icon={MenuFilter}
        label={filterLabel}
        placeholder={filterLabel}
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        data-testid={`${testIdPrefix}-filter`}
      />
      <div css={visuallyHiddenCss} role="status" aria-live="polite">
        {filterText.trim()
          ? t('add-remove-fields-dialog.results', '{count, plural, one {# result} other {# results}}', {
              count: resultCount,
            })
          : ''}
      </div>
      <Separator size="xxs" />
      {filteredGroups.map((group) => (
        <CheckboxGroup label={group.label} key={group.label} largerLabel>
          <Divider />
          {group.options.map((option) => (
            <div key={option.value}>
              <Separator size="nano" />
              <Checkbox
                value={option.value}
                name={option.value}
                label={option.label}
                checked={checked.has(option.value)}
                onChange={() => toggle(option.value)}
                data-testid={`${testIdPrefix}-option-${option.value}`}
              />
            </div>
          ))}
        </CheckboxGroup>
      ))}
    </DialogOverlay>
  )
}

AddRemoveFieldsDialog.propTypes = {
  overlay: PropTypes.shape({ isOpen: PropTypes.bool, close: PropTypes.func }).isRequired,
  title: PropTypes.string.isRequired,
  filterLabel: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
    })
  ).isRequired,
  initialChecked: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  linkNamePrefix: PropTypes.string.isRequired,
  testIdPrefix: PropTypes.string.isRequired,
}

export default AddRemoveFieldsDialog
